import * as React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Link,
} from "@mui/material";
import { AutoAwesome, Close, Send, SupportAgent } from "@mui/icons-material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import moment from "moment";
import { notification } from "antd";
import { getRequest, postRequest } from "../../core/network";
import endpoints from "../../constants/endPoints.json";
import { main_button, MainInput } from "./Styles";
import PasswordVerificationModal from "./PasswordVerificationModal";
import { refreshPassword } from "../store/actions/authActions";
import { pulseSubscribe, pulseUnsubscribe } from "../store/actions/dataActions";

const markdownSx = {
  "& p": { margin: 0, marginBottom: 1, "&:last-child": { marginBottom: 0 } },
  "& ul, & ol": { margin: 0, paddingInlineStart: 2 },
  "& code": {
    fontSize: "0.85em",
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: 1,
    px: 0.5,
  },
};

export const PulseChat = ({
  variant = "page",
  onClose,
  user,
  token,
  isLoading,
  refreshPassword,
  pulseSubscribe,
  pulseUnsubscribe,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language === "ar";
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const [bootstrap, setBootstrap] = React.useState(null);
  const [entitlement, setEntitlement] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [loadingBootstrap, setLoadingBootstrap] = React.useState(true);
  const [unavailable, setUnavailable] = React.useState(false);
  const [todayLabel, setTodayLabel] = React.useState("");
  const [activities, setActivities] = React.useState([]);
  const [ticketCreated, setTicketCreated] = React.useState(null);
  const [upgradeOpen, setUpgradeOpen] = React.useState(false);
  const [passwordMode, setPasswordMode] = React.useState(null);

  const applyEntitlement = React.useCallback((next) => {
    if (next) setEntitlement(next);
  }, []);

  const loadBootstrap = React.useCallback(async () => {
    if (!token) return;
    setLoadingBootstrap(true);
    try {
      const response = await getRequest(undefined, undefined, token, endpoints.partner.pulse.bootstrap);
      const data = response.data;
      setBootstrap(data);
      applyEntitlement(data.entitlement);
      if (Array.isArray(data.messages)) {
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
      setUnavailable(false);
    } catch (err) {
      if (err?.response?.status === 503 || err?.response?.data?.code === "OPENROUTER_NOT_CONFIGURED") {
        setUnavailable(true);
      } else {
        notification.error({ message: err?.response?.data?.message || t("pulse.loadFailed") });
      }
    } finally {
      setLoadingBootstrap(false);
    }
  }, [token, applyEntitlement, t]);

  React.useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  React.useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, activities]);

  const refreshEntitlement = React.useCallback(async () => {
    if (!token) return;
    try {
      const response = await getRequest(undefined, undefined, token, endpoints.partner.pulse.entitlement);
      applyEntitlement(response.data?.entitlement);
    } catch {
      // ignore focus poll failures
    }
  }, [token, applyEntitlement]);

  React.useEffect(() => {
    const onFocus = () => refreshEntitlement();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshEntitlement]);

  const openUpgrade = () => setUpgradeOpen(true);

  const sendMessage = async (rawText) => {
    const content = (rawText ?? draft).trim();
    if (!content || sending) return;

    if (entitlement && !entitlement.canSendMessage) {
      openUpgrade();
      return;
    }

    const userMsg = { role: "user", content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setDraft("");
    setSending(true);
    setActivities([]);
    setTicketCreated(null);

    try {
      const response = await postRequest(
        { messages: nextMessages.slice(-20) },
        undefined,
        undefined,
        token,
        endpoints.partner.pulse.chat
      );
      const data = response.data;
      if (data.reply) {
        setMessages((prev) => [...prev, data.reply]);
      }
      applyEntitlement(data.entitlement);
      setActivities(Array.isArray(data.activities) ? data.activities : []);
      if (data.ticketCreated) setTicketCreated(data.ticketCreated);
      if (data.meta?.todayLabel) setTodayLabel(data.meta.todayLabel);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 429 || code === "PULSE_QUOTA_EXCEEDED") {
        applyEntitlement(err.response.data.entitlement);
        openUpgrade();
      } else if (code === "OPENROUTER_NOT_CONFIGURED" || err?.response?.status === 503) {
        setUnavailable(true);
        setMessages((prev) => prev.slice(0, -1));
      } else {
        notification.error({ message: err?.response?.data?.message || t("pulse.sendFailed") });
        setMessages((prev) => prev.slice(0, -1));
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handlePasswordVerify = async (password) => {
    try {
      const result = await refreshPassword(password);
      if (result.type !== "WAIT_SUCCESS") return;
      setPasswordMode(null);
      if (passwordMode === "subscribe") {
        const data = await pulseSubscribe();
        applyEntitlement(data?.entitlement);
        setUpgradeOpen(false);
      } else if (passwordMode === "unsubscribe") {
        const data = await pulseUnsubscribe();
        applyEntitlement(data?.entitlement);
      }
    } catch {
      // notification already shown
    }
  };

  const planLabel = entitlement?.unlimited
    ? t("pulse.planUnlimited")
    : t("pulse.planFree");

  const quotaLabel = entitlement?.unlimited
    ? entitlement.subscription?.recurring
      ? t("pulse.billedMonthly")
      : entitlement.subscription?.expiresAt
        ? t("pulse.renewsOn", { date: moment(entitlement.subscription.expiresAt).format("D MMM") })
        : t("pulse.planUnlimited")
    : entitlement?.remainingToday != null
      ? t("pulse.remainingToday", { count: entitlement.remainingToday })
      : "";

  const upgrade = entitlement?.upgrade || bootstrap?.entitlement?.upgrade;
  const price = upgrade?.priceEgp ?? bootstrap?.limits?.unlimitedPriceEgp;

  const renderBody = () => {
    if (loadingBootstrap) {
      return (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress sx={{ color: "var(--primaryColor)" }} />
        </Box>
      );
    }

    if (unavailable) {
      return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 3, textAlign: "center", gap: 1 }}>
          <AutoAwesome sx={{ fontSize: 40, color: "var(--primaryColor)" }} />
          <Typography fontWeight={700} sx={{ color: "var(--textPrimary)" }}>{t("pulse.comingSoonTitle")}</Typography>
          <Typography sx={{ color: "var(--textSecondary)" }}>{t("pulse.comingSoonBody")}</Typography>
        </Box>
      );
    }

    const empty = messages.length === 0;

    return (
      <>
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {todayLabel && (
            <Typography variant="caption" sx={{ color: "var(--textSecondary)", textAlign: "center" }}>
              {todayLabel}
            </Typography>
          )}

          {empty && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: "var(--textPrimary)", mb: 2, whiteSpace: "pre-wrap" }}>
                {bootstrap?.welcome}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(bootstrap?.suggestedPrompts || []).map((prompt) => (
                  <Chip
                    key={prompt.id}
                    label={prompt.label}
                    onClick={() => sendMessage(prompt.message)}
                    sx={{
                      color: "var(--textPrimary)",
                      borderColor: "var(--borderColor)",
                      backgroundColor: "var(--card-solid)",
                      "&:hover": { backgroundColor: "var(--hoverBg)" },
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {messages.map((msg, index) => {
            const isUser = msg.role === "user";
            const isLastAssistant = !isUser && index === messages.length - 1;
            return (
              <Box
                key={`${msg.role}-${index}`}
                sx={{
                  alignSelf: isUser ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                }}
              >
                <Box
                  className={isUser ? "cz-pulse-bubble cz-pulse-bubble-user" : "cz-pulse-bubble cz-pulse-bubble-assistant"}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: isUser ? "var(--primaryColor)" : "var(--card-solid)",
                    color: isUser ? "#ffeaea" : "var(--textPrimary)",
                    border: isUser ? "none" : "1px solid var(--borderColor)",
                    boxShadow: isUser ? "none" : "0 4px 14px var(--sh30)",
                    ...markdownSx,
                    "& code": {
                      ...markdownSx["& code"],
                      backgroundColor: "var(--s08)",
                      color: "inherit",
                    },
                    "& a": { color: isUser ? "#ffeaea" : "var(--primaryColor)" },
                    "& p, & li, & strong, & em": { color: "inherit" },
                  }}
                >
                  {isUser ? (
                    <div className="cz-pulse-bubble-text" style={{ whiteSpace: "pre-wrap" }}>
                      {msg.content}
                    </div>
                  ) : (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  )}
                </Box>
                {isLastAssistant && activities.length > 0 && (
                  <Typography variant="caption" sx={{ color: "var(--textSecondary)", display: "block", mt: 0.5 }}>
                    {activities.map((a) => a.label).filter(Boolean).join(" · ")}
                  </Typography>
                )}
              </Box>
            );
          })}

          {sending && (
            <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
              {t("pulse.checking")}
            </Typography>
          )}

          {ticketCreated && (
            <Alert
              severity="success"
              icon={<SupportAgent />}
              sx={{ backgroundColor: "var(--card-solid)", color: "var(--textPrimary)" }}
            >
              {t("pulse.ticketCreated")}{" "}
              <Link
                component="button"
                onClick={() => {
                  onClose?.();
                  navigate(`/help-desk?ticketId=${ticketCreated.id}`);
                }}
                sx={{ color: "var(--primaryColor)" }}
              >
                {t("pulse.openTicket")}
              </Link>
            </Alert>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: "1px solid var(--borderColor)" }}>
          {!entitlement?.canSendMessage && !entitlement?.unlimited && (
            <Button fullWidth sx={{ ...main_button, mb: 1 }} onClick={openUpgrade}>
              {t("pulse.upgradeCta")}
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <MainInput
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder={t("pulse.placeholder")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || unavailable}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={sending || !draft.trim() || unavailable}
              sx={{ color: "var(--primaryColor)", mb: 0.5 }}
              aria-label={t("pulse.send")}
            >
              <Send sx={{ transform: isRtl ? "scaleX(-1)" : "none" }} />
            </IconButton>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "var(--card-solid)", color: "var(--textPrimary)" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid var(--borderColor)",
        }}
      >
        <AutoAwesome sx={{ color: "var(--primaryColor)" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ color: "var(--textPrimary)" }}>
            {t("pulse.title")}
          </Typography>
          <Typography variant="caption" sx={{ color: "var(--textSecondary)" }}>
            {planLabel}{quotaLabel ? ` · ${quotaLabel}` : ""}
          </Typography>
        </Box>
        {entitlement?.unlimited && (
          <Button
            size="small"
            onClick={() => setPasswordMode("unsubscribe")}
            sx={{ color: "var(--errorColor, #f44336)", textTransform: "none" }}
          >
            {t("pulse.unsubscribe")}
          </Button>
        )}
        {variant === "drawer" && (
          <IconButton onClick={onClose} sx={{ color: "var(--textPrimary)" }} aria-label={t("common.close")}>
            <Close />
          </IconButton>
        )}
      </Box>

      {renderBody()}

      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiDialog-paper": { backgroundColor: "var(--card-solid)", color: "var(--textPrimary)" } }}
      >
        <DialogTitle>{upgrade?.headline || t("pulse.upgradeTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--textSecondary)", mb: 2 }}>
            {upgrade?.description || t("pulse.upgradeBody")}
          </Typography>
          {price != null && (
            <Typography fontWeight={700} sx={{ color: "var(--textPrimary)" }}>
              {t("pulse.pricePerMonth", { price })}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: "var(--textSecondary)", mt: 1 }}>
            {t("pulse.billingNote")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUpgradeOpen(false)} sx={{ color: "var(--textPrimary)" }}>
            {t("common.cancel")}
          </Button>
          <Button sx={{ ...main_button }} onClick={() => setPasswordMode("subscribe")}>
            {t("pulse.subscribe")}
          </Button>
        </DialogActions>
      </Dialog>

      <PasswordVerificationModal
        open={Boolean(passwordMode)}
        onClose={() => setPasswordMode(null)}
        onVerify={handlePasswordVerify}
        isLoading={isLoading}
        title={passwordMode === "unsubscribe" ? t("pulse.verifyUnsubscribeTitle") : t("pulse.verifySubscribeTitle")}
        message={passwordMode === "unsubscribe" ? t("pulse.verifyUnsubscribeMessage") : t("pulse.verifySubscribeMessage")}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  token: state?.auth?.token,
  isLoading: state?.wait?.isLoading || state?.auth?.isLoading,
});

const mapDispatchToProps = { refreshPassword, pulseSubscribe, pulseUnsubscribe };

export default connect(mapStateToProps, mapDispatchToProps)(PulseChat);
